import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  MoreHorizontal,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import { apiClient } from '@/services/api-client';
import type { Persona, PaginatedResponse } from '@/types';

interface PersonaFormData {
  code: string;
  name: string;
  attributes: string;
  targetCustomers: string;
  painPoints: string;
  keyMotivations: string;
  isActive: boolean;
  sortOrder: number;
}

const emptyFormData: PersonaFormData = {
  code: '',
  name: '',
  attributes: '',
  targetCustomers: '',
  painPoints: '',
  keyMotivations: '',
  isActive: true,
  sortOrder: 0,
};

export function PersonasPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Dialog states
  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null);
  const [formData, setFormData] = useState<PersonaFormData>(emptyFormData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch personas
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin', 'personas', page, search, statusFilter],
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(page),
        limit: '10',
      };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.isActive = statusFilter;

      const response = await apiClient.get<PaginatedResponse<Persona>>(
        '/api/v1/admin/personas',
        params
      );
      if (!response.success) throw new Error(response.error?.message);

      return {
        items: (response.data as unknown as Persona[]) || [],
        meta: response.meta || { page: 1, limit: 10, total: 0, totalPages: 0 },
      } as PaginatedResponse<Persona>;
    },
  });

  // Open create dialog
  const handleCreate = () => {
    setFormData(emptyFormData);
    setIsEditing(false);
    setSelectedPersona(null);
    setFormOpen(true);
  };

  // Open edit dialog
  const handleEdit = (persona: Persona) => {
    setFormData({
      code: persona.code,
      name: persona.name,
      attributes: persona.attributes || '',
      targetCustomers: persona.targetCustomers || '',
      painPoints: persona.painPoints || '',
      keyMotivations: persona.keyMotivations || '',
      isActive: persona.isActive,
      sortOrder: persona.sortOrder,
    });
    setIsEditing(true);
    setSelectedPersona(persona);
    setFormOpen(true);
  };

  // Save (create or update)
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        attributes: formData.attributes || undefined,
        targetCustomers: formData.targetCustomers || undefined,
        painPoints: formData.painPoints || undefined,
        keyMotivations: formData.keyMotivations || undefined,
      };

      let response;
      if (isEditing && selectedPersona) {
        response = await apiClient.put(`/api/v1/admin/personas/${selectedPersona.id}`, payload);
      } else {
        response = await apiClient.post('/api/v1/admin/personas', payload);
      }

      if (response.success) {
        setFormOpen(false);
        refetch();
      }
    } catch {
      // Network error handled by API client
    } finally {
      setIsSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!selectedPersona) return;
    setIsDeleting(true);
    try {
      const response = await apiClient.delete(`/api/v1/admin/personas/${selectedPersona.id}`);
      if (response.success) {
        setDeleteOpen(false);
        setSelectedPersona(null);
        refetch();
      }
    } catch {
      // Network error
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setPage(1);
  };

  const hasFilters = search || statusFilter !== 'all';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-foreground">Personas</h1>
          <p className="text-muted-foreground">
            Manage user personas and archetypes that drive story card visibility
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Persona
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, code, or target customers..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="mr-1 h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Personas Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display">All Personas</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-14 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : !data || data.items.length === 0 ? (
            <div className="flex h-40 items-center justify-center text-muted-foreground">
              No personas found
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[80px]">Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Target Customers</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[70px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((persona) => (
                      <TableRow
                        key={persona.id}
                        className={!persona.isActive ? 'opacity-60' : undefined}
                      >
                        <TableCell>
                          <Badge variant="outline" className="font-mono">
                            {persona.code}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{persona.name}</TableCell>
                        <TableCell className="max-w-[300px] truncate text-muted-foreground">
                          {persona.targetCustomers || '—'}
                        </TableCell>
                        <TableCell>
                          {persona.isActive ? (
                            <Badge variant="success">Active</Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedPersona(persona);
                                  setDetailOpen(true);
                                }}
                              >
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEdit(persona)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedPersona(persona);
                                  setDeleteOpen(true);
                                }}
                                className="text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-2 py-4">
                <p className="text-sm text-muted-foreground">
                  Showing {(page - 1) * data.meta.limit + 1} to{' '}
                  {Math.min(page * data.meta.limit, data.meta.total)} of {data.meta.total} personas
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {data.meta.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page >= data.meta.totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              {selectedPersona?.name}
              <Badge variant="outline" className="ml-2 font-mono">
                {selectedPersona?.code}
              </Badge>
            </DialogTitle>
            <DialogDescription>Persona details and configuration</DialogDescription>
          </DialogHeader>
          {selectedPersona && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-semibold text-foreground">Attributes</Label>
                <p className="mt-1 whitespace-pre-wrap text-sm font-normal text-muted-foreground">
                  {selectedPersona.attributes || 'Not specified'}
                </p>
              </div>
              <div>
                <Label className="text-sm font-semibold text-foreground">Target Customers</Label>
                <p className="mt-1 whitespace-pre-wrap text-sm font-normal text-muted-foreground">
                  {selectedPersona.targetCustomers || 'Not specified'}
                </p>
              </div>
              <div>
                <Label className="text-sm font-semibold text-foreground">Pain Points</Label>
                <p className="mt-1 whitespace-pre-wrap text-sm font-normal text-muted-foreground">
                  {selectedPersona.painPoints || 'Not specified'}
                </p>
              </div>
              <div>
                <Label className="text-sm font-semibold text-foreground">Key Motivations</Label>
                <p className="mt-1 whitespace-pre-wrap text-sm font-normal text-muted-foreground">
                  {selectedPersona.keyMotivations || 'Not specified'}
                </p>
              </div>
              <div className="flex gap-6 text-sm text-muted-foreground">
                <span>
                  Status:{' '}
                  {selectedPersona.isActive ? (
                    <Badge variant="success">Active</Badge>
                  ) : (
                    <Badge variant="secondary">Inactive</Badge>
                  )}
                </span>
                <span>Created: {format(new Date(selectedPersona.createdAt), 'MMM d, yyyy')}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {isEditing ? 'Edit Persona' : 'Create Persona'}
            </DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Update the persona details below'
                : 'Fill in the details to create a new persona'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">Code</Label>
                <Input
                  id="code"
                  placeholder="e.g. RP"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  maxLength={10}
                  disabled={isEditing}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="e.g. Retired Professional"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="attributes">Attributes</Label>
              <Textarea
                id="attributes"
                placeholder="Key traits and characteristics..."
                value={formData.attributes}
                onChange={(e) => setFormData({ ...formData, attributes: e.target.value })}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="targetCustomers">Target Customers</Label>
              <Textarea
                id="targetCustomers"
                placeholder="Who this persona represents..."
                value={formData.targetCustomers}
                onChange={(e) => setFormData({ ...formData, targetCustomers: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="painPoints">Pain Points</Label>
              <Textarea
                id="painPoints"
                placeholder="What challenges they face..."
                value={formData.painPoints}
                onChange={(e) => setFormData({ ...formData, painPoints: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="keyMotivations">Key Motivations</Label>
              <Textarea
                id="keyMotivations"
                placeholder="What drives them..."
                value={formData.keyMotivations}
                onChange={(e) => setFormData({ ...formData, keyMotivations: e.target.value })}
                rows={2}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="isActive">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !formData.code || !formData.name}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Save Changes' : 'Create Persona'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Persona</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the persona "{selectedPersona?.name}" (
              {selectedPersona?.code})? This will also remove all story card associations. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
